import { FC, useMemo, useState } from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import { FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { AddMerchantApplication } from '@prisma/client';

import { useMutation } from '@redwoodjs/web';

import { Flex } from 'src/components/Flex';
import { ConfirmationModal } from 'src/components/Modals';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import {
    EMAIL_DOCUMENT_FOR_SIGNING,
    GENERATE_DOCUMENT_FOR_SIGNING,
} from 'src/graphql/mutations/documentForSigning.mutation';

interface SendApplicationSignatureModalProps {
    isModalOpen: boolean;
    handleCloseModal: () => void;
    application: AddMerchantApplication;
}

const SendApplicationSignatureModal: FC<SendApplicationSignatureModalProps> = ({
    isModalOpen,
    handleCloseModal,
    application,
}) => {
    const [email, setEmail] = useState('');
    const [, setSigningUrl] = useState('');
    const [emailSent, setEmailSent] = useState(false);
    const [actionType] = useState<'sendEmail' | 'generateAgreement'>('sendEmail');
    const ACTION_TYPE_SEND_EMAIL = 'sendEmail';
    const ACTION_TYPE_GENERATE_AGREEMENT = 'generateAgreement';
    const [selectedPrincipalIndex, setSelectedPrincipalIndex] = useState<number | null>(null);
    const isAddService = !!application?.isServiceApplication;

    const [generateOnlineDocumentation, { loading: isGenerating }] = useMutation(
        GENERATE_DOCUMENT_FOR_SIGNING,
        {
            onError: (error) => {
                showErrorToast(
                    error.message ?? 'We were unable to generate your on-screen agreement'
                );
            },
            onCompleted: ({ displayDocumentsForSigningOnScreen }) => {
                setSigningUrl(displayDocumentsForSigningOnScreen?.url);
            },
        }
    );

    const beneficialOwnerEmailAddresses = useMemo(() => {
        return (application?.merchantInformation?.principals ?? []).map((person, index) => {
            return {
                email: person.email,
                name: `${person.firstName} ${person.lastName}`,
                index,
            };
        });
    }, [application]);

    const handleSubmit = async () => {
        if (actionType === ACTION_TYPE_SEND_EMAIL && !isSending && !emailSent) {
            await emailDocumentForSigning({
                variables: {
                    input: {
                        applicationId: application.id,
                        membersInfo: [{ email }],
                        selectedPrincipalIndex,
                    },
                },
            });
        } else if (actionType === ACTION_TYPE_GENERATE_AGREEMENT && !isGenerating) {
            await generateOnlineDocumentation({
                variables: {
                    input: {
                        applicationId: application.id,
                        membersInfo: [{ email }],
                        selectedPrincipalIndex,
                    },
                },
            });
        }
    };

    const [emailDocumentForSigning, { loading: isSending }] = useMutation(
        EMAIL_DOCUMENT_FOR_SIGNING,
        {
            onError: (error) => {
                showErrorToast(error.message ?? 'We were unable to send your agreement');
            },
            onCompleted: () => {
                showSuccessToast('Check your inbox to complete the agreement signature process');
                setEmailSent(true);
            },
        }
    );
    const loadingModalButtonText = isAddService ? 'Service' : 'Merchant';

    return (
        <ConfirmationModal
            title={
                <Typography variant="h2" component="h2" align="center">
                    {actionType === ACTION_TYPE_SEND_EMAIL
                        ? 'Select a principal to receive the agreement via email:'
                        : 'Select a principal to generate the on-screen agreement:'}
                </Typography>
            }
            message={
                <>
                    <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{
                            textAlign: 'center',
                        }}
                    >
                        Successful completion will create the
                        {isAddService ? ' service' : ' merchant'}...
                    </Typography>
                    <Flex gap={1} flexDirection="column" alignItems={'center'}>
                        <FormControl>
                            <RadioGroup
                                name="beneficialOwnerEmailAddresses"
                                onChange={(e) => {
                                    const selectedIndex = beneficialOwnerEmailAddresses.findIndex(
                                        (p) => {
                                            return p.email === e.target.value;
                                        }
                                    );
                                    setEmail(e.target.value);
                                    setSelectedPrincipalIndex(selectedIndex);
                                }}
                                value={email}
                            >
                                {beneficialOwnerEmailAddresses.map((person, index) => {
                                    return (
                                        <FormControlLabel
                                            key={person.email}
                                            value={person.email}
                                            control={<Radio />}
                                            label={`${person.name} - (${person.email})`}
                                            disabled={
                                                (emailSent &&
                                                    actionType === ACTION_TYPE_SEND_EMAIL) ||
                                                (actionType === ACTION_TYPE_GENERATE_AGREEMENT &&
                                                    selectedPrincipalIndex !== index &&
                                                    emailSent)
                                            }
                                        />
                                    );
                                })}
                            </RadioGroup>
                        </FormControl>
                        <LoadingButton
                            onClick={handleSubmit}
                            disabled={
                                selectedPrincipalIndex === null ||
                                (actionType === ACTION_TYPE_SEND_EMAIL && emailSent) ||
                                isGenerating ||
                                isSending
                            }
                            loading={isGenerating || isSending}
                            loadingPosition="start"
                            variant="npi-primary"
                            color="primary"
                            sx={{
                                mt: 2,
                                bgcolor: 'primary.main',
                                '.MuiLoadingButton-loadingIndicator': {
                                    color: 'primary.contrastText',
                                },
                            }}
                        >
                            {actionType === ACTION_TYPE_SEND_EMAIL
                                ? `Send Email & Create ${loadingModalButtonText}`
                                : `Generate Agreement & Create ${loadingModalButtonText}`}
                        </LoadingButton>
                    </Flex>
                </>
            }
            isOpen={isModalOpen}
            onCancel={() => {
                return handleCloseModal();
            }}
            modalWidth="md"
            hideConfirmButton
            hideCancelButton
        />
    );
};

export default SendApplicationSignatureModal;
